import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  fetchJobDetails,
  fetchCareerDetailsBanner,
  createJobApplication
} from "../Adapter/PageAdapter";
import useSeo from "../hooks/useSeo";
import SeoHelmet from "../components/SeoHelmet";
import {
  Dialog,
  TextField,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { Toaster, toast } from 'sonner';
import CareerBg from "../assets/images/Career.jpg";
import arrSvg from "../assets/images/arrw.svg";
import { baseImage_path } from "../variables/Variables";

const CareerDetails = () => {
  const { slug } = useParams(); // Get the job ID from the URL
  const [bannerData, setBannerData] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const seoData = useSeo(fetchJobDetails);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    resume: null,
    message: "",
    description: jobDetails?.description || "",
  });

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  useEffect(() => {
    const getJobDetails = async () => {
      try {
        const bannerResponse = await fetchCareerDetailsBanner();
        setBannerData(bannerResponse.data);

        const response = await fetchJobDetails(slug);
        if (response?.data) {
          setJobDetails(response.data);
          setJobId(response.data.id);

          setFormData((prev) => ({
            ...prev,
            description: stripHtml(response.data.description || ""),
          }));
        } else {
          console.error("No job found for this slug.");
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
      } finally {
        setLoading(false);
      }
    };

    getJobDetails();
  }, [slug]);

  if (loading) {
    return <p>Loading job details...</p>;
  }

  if (!jobDetails) {
    return <p>Job details not found for ID: {jobId}</p>;
  }

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      resume: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formBody = new FormData();
    formBody.append("first_name", formData.first_name);
    formBody.append("last_name", formData.last_name);
    formBody.append("email", formData.email);
    formBody.append("phone", formData.phone);
    formBody.append("resume", formData.resume);
    formBody.append("message", formData.message);
    formBody.append("description", formData.description);
    formBody.append("job_id", jobId);
    formBody.append("exp_required", jobDetails.exp_required || "");

    try {
      const response = await createJobApplication(formBody);
      if (response?.message) {
        toast.success(response.message);
        setOpen(false); // Close the modal on success
        setFormData({
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          resume: null,
          message: "",
          description: jobDetails?.description || "",
        });
      }
    } catch (error) {
      toast.error("Failed to submit the application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Banner Section */}
      <section className="banner-spac">
        <div className="cust-container">
          <div className="banner-box">
            {jobDetails?.banner_img ? (
              <img
                src={`${baseImage_path}${jobDetails.banner_img.replace(/^\./, "")}`}
                alt={jobDetails.title || "Career Banner"}
              />
            ) : (
              <img src={CareerBg} alt="Default Career Banner" />
            )}
            <div className="ban-con">
              <h1>{jobDetails.title || "Job Title"}</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Job Description Section */}
      <section className="jobdescriptionsec mrgntop70 margnbot90">
        <div className="cust-container">
          <div className="jobdescription-inner">
            <div className="row">
              <div className="col-md-12">
                <div className="host-det mobtopmarg">
                  <h4 className="adjustTitle">Job Responsibilities</h4>
                  <div className="ck-content"
                    dangerouslySetInnerHTML={{
                      __html:
                        jobDetails.description || "No description provided.",
                    }}
                  />

                  <h4 className="adjustTitle">Department</h4>
                  <p>{jobDetails.department || "Not specified."}</p>

                  <h4 className="adjustTitle">Designation</h4>
                  <p>{jobDetails.designation || "Not specified."}</p>

                  <h4 className="adjustTitle">Skills Required</h4>
                  <p>{jobDetails.skills || "Not specified."}</p>

                  <h4 className="adjustTitle">Education</h4>
                  <p>{jobDetails.education || "Not specified."}</p>

                  <h4 className="adjustTitle">Experience Required</h4>
                  <p>
                    {jobDetails.exp_required
                      ? `${jobDetails.exp_required} years`
                      : "Not specified."}
                  </p>

                  <h4 className="adjustTitle">Posted By</h4>
                  <p>{jobDetails.posted_by || "HR"}</p>

                  {/* Apply Now Button */}
                  <div className="jdapplybtn" style={{ marginBottom: "10px" }}>
                    <button
                      className="btn with-color with-arr"
                      onClick={handleOpen}
                    >
                      <img src={arrSvg} alt="Arrow" /> Apply Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <ToastContainer /> */}
      <Toaster position="top-right" />

      {/* Application Modal */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <Box p={3}>
            <h3>Apply for the Job</h3>
            <TextField
              fullWidth
              margin="normal"
              label="First Name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Last Name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Phone Number"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Message"
              name="message"
              multiline
              rows={3}
              value={formData.message}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Description"
              name="description"
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange}
            />
            <input
              type="file"
              name="resume"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              required
              style={{ margin: "16px 0" }}
            />
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button onClick={handleClose} style={{ marginRight: "8px" }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                className="btn with-color with-arr"
              >
                {loading ? <CircularProgress size={24} /> : "Submit"}
              </Button>
            </Box>
          </Box>
        </form>
      </Dialog>
    </>
  );
};

export default CareerDetails;
